import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import { supabase } from '../supabaseClient';
import pdfjs from '../lib/pdfjs';
import { formatDateBritish, todayIso } from '../lib/compliance';
import { supplierIdentity } from '../lib/supplierIdentity';
import { pagesSeenFrom, currentPageFrom } from '../lib/pdfPagesSeen';
import { verifyMetaBeforeSigning, contentHashForMeta } from '../lib/documentVersion';
import SignatureCapture from '../components/compliance/SignatureCapture';

// Full-page PDF signing (brief-pdf-signing-fullpage). Replaces the modal for
// PDFs of any length. Three columns: page thumbnails (navigate + viewed +
// field markers) · viewer (fit-to-width, scroll-to-view marks a page read) ·
// signing rail (the fields, a both-conditions gate, submit). Submit stays gated
// on EVERY page being viewed AND every required field completed. "Save & finish
// later" persists per user so an 11-page doc can be signed across sittings; a
// delegate (different user) starts fresh. Signing flattens the field values +
// signature onto a copy of the source PDF at the placed coords (field_layout is
// already PDF points / bottom-left origin), then records the submission.

const BUCKET = 'supplier-uploads';
const FONT = "font-['Metropolis']";
const NEUTRAL = '#C3C9AF';
const GREEN = '#2F6E14';

export default function PdfSignPage({ document: docMeta, supplier, supplierId, supplierUserId, signer, onExit, onSuccess }) {
  const layout = useMemo(() => Array.isArray(docMeta?.fieldLayout) ? docMeta.fieldLayout : [], [docMeta]);
  const todayLabel = useMemo(() => formatDateBritish(todayIso()), []);
  const version = docMeta?.versionValue || 0;

  // Through the shared helper: the columns are `supplier_reg_number`,
  // `supplier_vat_number` and five separate address columns. Reading
  // `reg_number`/`vat_number`/`address` (as this did) prefilled BLANK on every
  // PDF a supplier has ever signed, with nothing to show it had failed.
  const identityDefaults = useMemo(
    () => supplierIdentity(supplier, { todayLabel }),
    [supplier, todayLabel],
  );

  const [values, setValues] = useState(() => {
    const v = {};
    for (const f of (Array.isArray(docMeta?.fieldLayout) ? docMeta.fieldLayout : [])) {
      if (f.type === 'identity' && f.prefill_source) v[f.id] = identityDefaults[f.prefill_source] || '';
    }
    return v;
  });
  const [authorityConfirmed, setAuthorityConfirmed] = useState(false);
  const [viewedPages, setViewedPages] = useState(() => new Set());
  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rendered, setRendered] = useState(false);
  const [signingField, setSigningField] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [savingLater, setSavingLater] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [zoomMode, setZoomMode] = useState('fit'); // 'fit' | 'custom'
  const [customScale, setCustomScale] = useState(1); // factor relative to fit (1 = fit)
  const [resizeNonce, setResizeNonce] = useState(0);
  const [, bump] = useState(0);

  const pdfRef = useRef(null);
  const sourceBytesRef = useRef(null);
  const viewerRef = useRef(null);
  const pagesWrapRef = useRef(null);
  const pagesRef = useRef([]);   // [{ el }]
  const thumbRefs = useRef([]);  // canvas per page
  const savedLastPageRef = useRef(1);
  const restoredRef = useRef(false);

  const setValue = useCallback((id, v) => setValues(prev => ({ ...prev, [id]: v })), []);

  // ---- Load the PDF ----
  useEffect(() => {
    if (!docMeta?.sourcePdfPath) return;
    let cancelled = false;
    (async () => {
      try {
        const bytes = await fetch(docMeta.sourcePdfPath).then(r => { if (!r.ok) throw new Error('fetch'); return r.arrayBuffer(); });
        if (cancelled) return;
        sourceBytesRef.current = bytes;
        const pdf = await pdfjs.getDocument({ data: bytes.slice(0) }).promise;
        if (cancelled) return;
        pdfRef.current = pdf;
        setNumPages(pdf.numPages);
      } catch (e) {
        if (!cancelled) setError(`Could not load the document: ${e?.message || e}`);
      }
    })();
    return () => { cancelled = true; };
  }, [docMeta?.sourcePdfPath]);

  // ---- Load saved progress (per user; a delegate has no row → fresh) ----
  useEffect(() => {
    if (!supplierUserId || !docMeta?.key) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase.from('pdf_sign_progress')
        .select('pages_viewed, field_values, authority_confirmed, last_page')
        .eq('supplier_user_id', supplierUserId).eq('document_key', docMeta.key).eq('document_version', version)
        .maybeSingle();
      if (cancelled || !data) return;
      if (Array.isArray(data.pages_viewed) && data.pages_viewed.length) setViewedPages(new Set(data.pages_viewed));
      if (data.field_values && typeof data.field_values === 'object') setValues(prev => ({ ...prev, ...data.field_values }));
      if (data.authority_confirmed) setAuthorityConfirmed(true);
      savedLastPageRef.current = data.last_page || 1;
    })();
    return () => { cancelled = true; };
  }, [supplierUserId, docMeta?.key, version]);

  // ---- Render the viewer pages (fit-to-width; re-renders on zoom/resize) ----
  useEffect(() => {
    const pdf = pdfRef.current, wrap = pagesWrapRef.current, viewer = viewerRef.current;
    if (!pdf || !wrap || !viewer || numPages === 0) return;
    let cancelled = false;
    (async () => {
      wrap.innerHTML = '';
      pagesRef.current = [];
      // Page-seen and current-page are BOTH measured on scroll now — see the
      // effect below. There used to be an IntersectionObserver here doing the
      // same job from ratio thresholds, which is what made the last page
      // unreadable; keeping it as well would leave two copies of one gate rule
      // free to drift apart.
      const avail = Math.max(320, (viewer.clientWidth || 800) - 48);
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      for (let i = 1; i <= pdf.numPages; i += 1) {
        if (cancelled) return;
        const page = await pdf.getPage(i);
        const base = page.getViewport({ scale: 1 });
        const fit = avail / base.width;
        const scale = fit * (zoomMode === 'fit' ? 1 : customScale);
        const vp = page.getViewport({ scale });
        const hi = page.getViewport({ scale: scale * dpr });
        const w = Math.round(vp.width), h = Math.round(vp.height);
        const box = window.document.createElement('div');
        box.style.position = 'relative'; box.style.margin = '0 auto 16px';
        box.style.width = `${w}px`; box.style.height = `${h}px`;
        box.style.background = '#fff';
        box.style.boxShadow = '0 2px 12px rgba(45,48,35,0.12)';
        box.setAttribute('data-page', String(i));
        const canvas = window.document.createElement('canvas');
        canvas.width = Math.round(hi.width); canvas.height = Math.round(hi.height);
        canvas.style.width = `${w}px`; canvas.style.height = `${h}px`; canvas.style.display = 'block';
        box.appendChild(canvas); wrap.appendChild(box);
        await page.render({ canvasContext: canvas.getContext('2d'), viewport: hi }).promise;

        // Show WHERE each field sits on the document, and what it is.
        // The fields are filled in the rail beside the page, so without this
        // the document itself is a wall of text with no indication that
        // anything on it is being asked of you — you have to infer which rail
        // control corresponds to which blank line. Markers are outlines with
        // the field's name; they are not editable (the rail owns the input),
        // and clicking one focuses its rail control.
        //
        // field_layout coords are PDF POINTS with a bottom-left origin, so y
        // flips against the CSS top-left origin.
        for (const f of layout) {
          if (f.page !== i) continue;
          const marker = window.document.createElement('div');
          marker.style.position = 'absolute';
          marker.style.left = `${f.x * scale}px`;
          marker.style.top = `${h - (f.y + f.h) * scale}px`;
          marker.style.width = `${f.w * scale}px`;
          marker.style.height = `${f.h * scale}px`;
          marker.style.border = '1.5px dashed #2E8FA1';
          marker.style.borderRadius = '4px';
          marker.style.background = 'rgba(46,143,161,0.07)';
          marker.style.display = 'flex';
          marker.style.alignItems = 'center';
          marker.style.justifyContent = 'center';
          marker.style.cursor = 'pointer';
          marker.style.overflow = 'hidden';
          marker.title = `${f.label}${f.required && f.type !== 'date_signed' ? ' (required)' : ''} — fill this in on the right`;
          const tag = window.document.createElement('span');
          tag.textContent = f.label || f.type;
          tag.style.font = "600 11px 'Metropolis', Helvetica, Arial, sans-serif";
          tag.style.color = '#1F6675';
          tag.style.padding = '0 6px';
          tag.style.whiteSpace = 'nowrap';
          tag.style.overflow = 'hidden';
          tag.style.textOverflow = 'ellipsis';
          marker.appendChild(tag);
          marker.addEventListener('click', () => {
            const el = window.document.getElementById(`railfield-${f.id}`);
            if (!el) return;
            el.scrollIntoView({ block: 'center', behavior: 'smooth' });
            el.focus?.();
          });
          box.appendChild(marker);
        }

        // The scroll pass below reads straight off pagesRef, so nothing needs
        // observing — this is the only registration a page requires.
        pagesRef.current[i] = { el: box };
      }
      if (cancelled) return;
      setRendered(true); bump(n => n + 1);
      if (!restoredRef.current && savedLastPageRef.current > 1 && pagesRef.current[savedLastPageRef.current]) {
        restoredRef.current = true;
        pagesRef.current[savedLastPageRef.current].el.scrollIntoView({ block: 'start' });
      }
    })();
    return () => { cancelled = true; };
    // `layout` is included so markers redraw if the field set arrives after
    // the first render pass.
  }, [numPages, zoomMode, customScale, resizeNonce, layout]);

  // ---- Page-seen, measured on scroll (NOT only on observer callbacks) ----
  //
  // THE LAST PAGE COULD NEVER BE MARKED READ, so "1 page still to read" stood
  // forever and Sign & Submit stayed disabled with the whole document read.
  //
  // The observer above gates on EITHER half the page being visible, OR the page
  // filling 60% of the viewport. That second test is the one a tall page has to
  // rely on — but it is only ever EVALUATED when intersectionRatio crosses one
  // of the configured thresholds. intersectionRatio is a fraction of the PAGE,
  // and at fit-to-width an A4 page is roughly twice the viewer's height, so the
  // ratio tops out around 0.44 and never reaches the 0.5 threshold above it.
  // The observer therefore goes SILENT from 0.25 upwards — exactly the range
  // where the page grows to fill the viewport and the test would start passing.
  //
  // Middle pages survive that because the pages around them keep crossing
  // thresholds and re-firing the callback while they are large. The last page
  // has nothing after it to scroll, so the final callback naming it arrives
  // while it is still only a quarter visible, and it is never looked at again.
  //
  // Measuring directly on scroll removes the dependence on threshold crossings
  // altogether. Same rule, evaluated continuously.
  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer || !rendered || numPages === 0) return undefined;
    let frame = 0;
    const measure = () => {
      frame = 0;
      const root = viewer.getBoundingClientRect();
      const rects = [];
      for (let n = 1; n <= numPages; n += 1) {
        const el = pagesRef.current[n]?.el;
        if (!el) continue;
        const r = el.getBoundingClientRect();
        rects.push({ page: n, top: r.top, bottom: r.bottom });
      }
      const atEnd = viewer.scrollTop + viewer.clientHeight >= viewer.scrollHeight - 2;
      const seen = pagesSeenFrom({ rects, root, atEnd, lastPage: numPages });
      if (seen.size) {
        setViewedPages(prev => {
          let changed = false;
          const next = new Set(prev);
          seen.forEach(n => { if (!next.has(n)) { next.add(n); changed = true; } });
          return changed ? next : prev;
        });
      }
      const cur = currentPageFrom({ rects, root });
      if (cur) setCurrentPage(cur);
    };
    const onScroll = () => { if (!frame) frame = window.requestAnimationFrame(measure); };
    viewer.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    measure();   // the document may already be short enough to be fully read
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      viewer.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [rendered, numPages, zoomMode, customScale, resizeNonce]);

  // ---- Thumbnails (rendered once) ----
  useEffect(() => {
    const pdf = pdfRef.current;
    if (!pdf || numPages === 0) return;
    let cancelled = false;
    (async () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      for (let i = 1; i <= pdf.numPages; i += 1) {
        if (cancelled) return;
        const canvas = thumbRefs.current[i];
        if (!canvas) continue;
        const page = await pdf.getPage(i);
        const base = page.getViewport({ scale: 1 });
        const scale = 116 / base.width;
        const hi = page.getViewport({ scale: scale * dpr });
        canvas.width = Math.round(hi.width); canvas.height = Math.round(hi.height);
        canvas.style.width = '100%'; canvas.style.height = 'auto';
        await page.render({ canvasContext: canvas.getContext('2d'), viewport: hi }).promise;
      }
    })();
    return () => { cancelled = true; };
  }, [numPages]);

  // Re-fit on window resize while in Fit mode.
  useEffect(() => {
    const onResize = () => setResizeNonce(n => n + 1);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // ---- Derived state ----
  const fieldPages = useMemo(() => new Set(layout.map(f => f.page)), [layout]);
  const signaturePage = useMemo(() => (layout.find(f => f.type === 'signature')?.page) || null, [layout]);
  const railFields = useMemo(() => layout.slice().sort((a, b) => (a.page - b.page) || (b.y - a.y) || (a.x - b.x)), [layout]);
  const requiredList = useMemo(() => layout.filter(f => f.required && f.type !== 'date_signed'), [layout]);
  const isDone = useCallback((f) => { const v = values[f.id]; return f.type === 'tick' ? !!v : (v != null && String(v).trim() !== ''); }, [values]);
  const requiredDone = requiredList.filter(isDone).length;
  const requiredFilled = requiredDone === requiredList.length;
  const totalFields = requiredList.length + 1; // + authority tick
  const doneFields = requiredDone + (authorityConfirmed ? 1 : 0);
  const allPagesViewed = numPages > 0 && viewedPages.size >= numPages;
  const pagesOutstanding = numPages > 0 ? Math.max(0, numPages - viewedPages.size) : 0;
  const canSubmit = rendered && allPagesViewed && requiredFilled && authorityConfirmed && !submitting;
  const companyLabel = identityDefaults.company_name || 'my organisation';

  // Both conditions, always stated (§5).
  const gateText = [
    !allPagesViewed ? `${pagesOutstanding} page${pagesOutstanding === 1 ? '' : 's'} still to read` : null,
    `${doneFields} of ${totalFields} field${totalFields === 1 ? '' : 's'} complete`,
  ].filter(Boolean).join(' · ');

  const jumpToPage = useCallback((n) => {
    const p = pagesRef.current[n];
    if (p?.el) p.el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  // ---- Save & finish later ----
  const persistProgress = useCallback(async () => {
    if (!supplierUserId || !supplierId || !docMeta?.key) return;
    const { error: e } = await supabase.from('pdf_sign_progress').upsert({
      supplier_id: supplierId, supplier_user_id: supplierUserId,
      document_key: docMeta.key, document_version: version,
      pages_viewed: Array.from(viewedPages), field_values: values,
      authority_confirmed: authorityConfirmed, last_page: currentPage,
      updated_at: new Date().toISOString(),
    }, { onConflict: 'supplier_user_id,document_key,document_version' });
    if (e) throw new Error(e.message);
  }, [supplierUserId, supplierId, docMeta, version, viewedPages, values, authorityConfirmed, currentPage]);

  const saveAndExit = useCallback(async () => {
    setSavingLater(true); setError(null);
    try { await persistProgress(); onExit?.(); }
    catch (e) { setError(`Could not save your progress: ${e.message}`); setSavingLater(false); }
  }, [persistProgress, onExit]);

  // ---- Submit: flatten onto the source PDF + record ----
  const handleSubmit = useCallback(async () => {
    if (!canSubmit || !supplierId || !docMeta?.key || !sourceBytesRef.current) return;
    setSubmitting(true); setError(null);
    try {
      // Evidence check before flattening anything (document-versioning §3): the
      // source PDF + field layout in front of the supplier must still hash to
      // what was published. Documents published before hashing existed report
      // 'unknown' and are not blocked.
      const evidence = verifyMetaBeforeSigning(docMeta, { pdfBytes: sourceBytesRef.current });
      if (evidence.state === 'mismatch') {
        throw new Error('This document has changed since it was published, so it can\'t be signed right now. Please tell Fusion 2K — they\'ll publish it properly as a new version.');
      }

      const signedAt = new Date().toISOString();
      const pdfDoc = await PDFDocument.load(sourceBytesRef.current.slice(0));
      const helv = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const pages = pdfDoc.getPages();
      const ink = rgb(0.07, 0.07, 0.07);
      for (const f of layout) {
        const page = pages[f.page - 1];
        if (!page) continue;
        if (f.type === 'signature') {
          const dataUrl = values[f.id];
          if (dataUrl && typeof dataUrl === 'string' && dataUrl.startsWith('data:image')) {
            const png = await pdfDoc.embedPng(dataUrl);
            page.drawImage(png, { x: f.x, y: f.y, width: f.w, height: f.h });
          }
          continue;
        }
        if (f.type === 'tick') {
          if (values[f.id]) page.drawText('X', { x: f.x + 2, y: f.y + 2, size: Math.max(8, f.h - 5), font: helv, color: ink });
          continue;
        }
        const text = f.type === 'date_signed' ? todayLabel : (values[f.id] || '');
        if (String(text).trim()) {
          const size = Math.min(11, Math.max(7, f.h - 4));
          page.drawText(String(text), { x: f.x + 1, y: f.y + Math.max(1, (f.h - size) / 2), size, font: helv, color: ink });
        }
      }
      const outBytes = await pdfDoc.save();
      const blob = new Blob([outBytes], { type: 'application/pdf' });
      const path = `${supplierId}/${docMeta.key}-pdfsign-${Date.now()}.pdf`;
      const { error: upErr } = await supabase.storage.from(BUCKET).upload(path, blob, { contentType: 'application/pdf', upsert: true });
      if (upErr) throw new Error(upErr.message);
      const { data: pub } = supabase.storage.from(BUCKET).getPublicUrl(path);
      const signedUrl = pub?.publicUrl || null;

      let createdBy = null;
      try { const { data: { user } } = await supabase.auth.getUser(); createdBy = user?.id || null; } catch { /* best-effort */ }

      const { error: subErr } = await supabase.from('hosted_form_submissions').insert({
        supplier_id: supplierId, document_key: docMeta.key, document_id: docMeta.documentId || null,
        document_version: docMeta.versionValue || null,
        responses: { method: 'pdf_sign', fields: values, pages_viewed: Array.from(viewedPages), authority_confirmed: authorityConfirmed },
        signer_name: signer?.name || '', signer_email: signer?.email || null,
        signed_at: signedAt, document_html_url: signedUrl, created_by: createdBy,
        // Hash of the source PDF + the field layout as signed — the signed
        // artefact above is the flattened copy; this proves what it came from.
        content_hash: evidence.actual || contentHashForMeta(docMeta, { pdfBytes: sourceBytesRef.current }),
      });
      if (subErr) throw new Error(subErr.message);

      const { error: sodErr } = await supabase.from('supplier_onboarding_documents').upsert({
        supplier_id: supplierId, document_key: docMeta.key,
        supplier_uploaded_file_url: signedUrl,
        supplier_uploaded_file_name: `${docMeta.label} — signed ${todayLabel}.pdf`,
        supplier_uploaded_at: signedAt,
        expiry_date: null, expiry_approved_at: null, expiry_approved_by: null, signed_at: null,
        review_decision: null, review_reason: null, review_notes: null, reviewed_at: null, reviewed_by: null,
        supplier_reply: null, supplier_reply_at: null, updated_at: signedAt,
      }, { onConflict: 'supplier_id,document_key' });
      if (sodErr) throw new Error(sodErr.message);

      // The signed artefact is the record now — clear the resume progress.
      if (supplierUserId) {
        await supabase.from('pdf_sign_progress').delete()
          .eq('supplier_user_id', supplierUserId).eq('document_key', docMeta.key).eq('document_version', version);
      }
      setSuccess(true);
      setTimeout(() => onSuccess?.(), 1500);
    } catch (e) {
      setError(e.message || 'Could not submit. Try again.');
      setSubmitting(false);
    }
  }, [canSubmit, supplierId, docMeta, layout, values, viewedPages, authorityConfirmed, signer, todayLabel, version, supplierUserId, onSuccess]);

  // Zoom controls — factor is relative to fit (1 = fit).
  const factor = zoomMode === 'fit' ? 1 : customScale;
  const zoomOut = () => { setZoomMode('custom'); setCustomScale(Math.max(0.5, Math.round((factor - 0.25) * 100) / 100)); };
  const zoomIn = () => { setZoomMode('custom'); setCustomScale(Math.min(3, Math.round((factor + 0.25) * 100) / 100)); };
  const zoomFit = () => { setZoomMode('fit'); setCustomScale(1); };
  const zoomLabel = zoomMode === 'fit' ? 'Fit' : `${Math.round(customScale * 100)}%`;

  if (success) {
    return (
      <div className="fixed inset-0 z-[70] bg-canvas flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl border border-line shadow-lift px-8 py-10 text-center max-w-[460px]">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full mb-4" style={{ backgroundColor: 'rgba(46,143,161,0.1)', border: '1.5px solid rgba(46,143,161,0.4)' }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#2E8FA1" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12" /></svg>
          </div>
          <h2 className={`${FONT} text-lg font-extrabold text-ink mb-2`}>Signed and submitted</h2>
          <p className={`${FONT} text-[12px] text-fusion-mid-grey leading-relaxed`}>Fusion 2K will review your signed {docMeta.label} and let you know once it's approved.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[70] bg-canvas flex flex-col">
      {/* Top bar */}
      <div className="flex items-center justify-between gap-4 px-5 py-2.5 border-b border-line bg-white flex-shrink-0">
        <div className="min-w-0 flex items-center gap-3">
          <button type="button" onClick={onExit}
            className={`${FONT} text-[11px] font-bold uppercase tracking-[0.14em] text-[#51534A] hover:text-fusion-mid-grey flex-shrink-0`}>← Back</button>
          <div className="min-w-0">
            <div className={`${FONT} text-[15px] font-bold text-ink truncate leading-tight`}>{docMeta.label}</div>
            {docMeta.description && <div className={`${FONT} text-[11px] text-[#51534A] truncate`}>{docMeta.description}</div>}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <div className="flex items-center gap-0.5 bg-[#f3f5ee] border border-line rounded-full px-1 py-0.5">
            <button type="button" onClick={zoomOut} disabled={factor <= 0.5} aria-label="Zoom out"
              className={`${FONT} w-7 h-7 rounded-full flex items-center justify-center text-[15px] font-bold text-[#51534A] hover:bg-white disabled:opacity-35`}>−</button>
            <button type="button" onClick={zoomFit} aria-label="Fit to width"
              className={`${FONT} min-w-[42px] text-center text-[11px] font-bold ${zoomMode === 'fit' ? 'text-[#2E8FA1]' : 'text-fusion-mid-grey'} hover:text-ink tabular-nums`}>{zoomLabel}</button>
            <button type="button" onClick={zoomIn} disabled={factor >= 3} aria-label="Zoom in"
              className={`${FONT} w-7 h-7 rounded-full flex items-center justify-center text-[15px] font-bold text-[#51534A] hover:bg-white disabled:opacity-35`}>+</button>
          </div>
          <button type="button" onClick={() => docMeta.sourcePdfPath && window.open(docMeta.sourcePdfPath, '_blank', 'noopener,noreferrer')}
            className={`${FONT} bg-white border border-line rounded-xl text-fusion-mid-grey text-xs font-semibold px-3.5 py-2 hover:border-[#DADCCD]`}>Download</button>
          <button type="button" onClick={saveAndExit} disabled={savingLater}
            className={`${FONT} bg-white border border-[#2E8FA1]/40 rounded-xl text-[#2E8FA1] text-xs font-semibold px-3.5 py-2 hover:border-[#2E8FA1] disabled:opacity-50`}>
            {savingLater ? 'Saving…' : 'Save & finish later'}
          </button>
        </div>
      </div>

      {error && <div className={`${FONT} text-[12px] text-[#E0290A] px-5 py-2 bg-[#E0290A]/[0.05] flex-shrink-0`}>{error}</div>}

      <div className="flex-1 flex min-h-0">
        {/* Thumbnails — a navigation landmark; hidden on smaller laptops (the
            rail is where the work happens), where a page control appears instead. */}
        <nav aria-label="Pages" className="hidden min-[1100px]:flex flex-col w-[152px] flex-shrink-0 border-r border-line bg-white">
          <div className={`${FONT} px-3 py-2.5 text-[11px] font-bold text-[#51534A] border-b border-line`}>
            Pages · {viewedPages.size} of {numPages} seen
          </div>
          <ol className="flex-1 overflow-y-auto p-2.5 flex flex-col gap-2.5 m-0 list-none">
            {Array.from({ length: numPages }, (_, i) => i + 1).map(n => {
              const seen = viewedPages.has(n);
              const hasField = fieldPages.has(n);
              const isCur = currentPage === n;
              return (
                <li key={n}>
                  <button type="button" onClick={() => jumpToPage(n)} aria-current={isCur ? 'page' : undefined}
                    className={`relative block w-full rounded-md overflow-hidden border-2 transition-colors ${isCur ? 'border-[#2E8FA1]' : 'border-[#E6EADB] hover:border-[#C3C9AF]'}`}>
                    <canvas ref={el => { thumbRefs.current[n] = el; }} className="block w-full bg-white" />
                    <span className={`${FONT} absolute top-1 left-1 text-[9px] font-bold px-1.5 py-0.5 rounded bg-black/55 text-white tabular-nums`}>{n}</span>
                    {hasField && (
                      <span className={`${FONT} absolute top-1 right-1 text-[8px] font-bold uppercase tracking-[0.08em] px-1 py-0.5 rounded bg-[#131313]/80 text-white`}>Fields</span>
                    )}
                    <span className={`${FONT} block text-[9px] font-bold uppercase tracking-[0.08em] text-center py-0.5 ${seen ? 'bg-[#E9F3DC] text-[#3B6D11]' : 'bg-[#F4F6EA] text-[#9BA08C]'}`}>
                      {seen ? '✓ Seen' : 'Unread'}
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>
        </nav>

        {/* Viewer */}
        <div ref={viewerRef} className="flex-1 overflow-auto bg-[#f3f5ee] p-6">
          {!rendered && <p className={`${FONT} text-[13px] text-fusion-mid-grey text-center py-12`}>Loading document…</p>}
          <div ref={pagesWrapRef} />
        </div>

        {/* Signing rail */}
        <aside className="w-[360px] max-[1279px]:w-[320px] flex-shrink-0 border-l border-line bg-white flex flex-col min-h-0">
          <div className="flex-1 overflow-y-auto p-4">
            <h2 className={`${FONT} text-[15px] font-extrabold text-ink mb-1`}>Sign this document</h2>

            {/* Small-screen page control (thumbnails hidden < 1100px) */}
            <div className={`${FONT} min-[1100px]:hidden flex items-center justify-between gap-2 mb-3 text-[11px] text-[#51534A]`}>
              <button type="button" onClick={() => jumpToPage(Math.max(1, currentPage - 1))} disabled={currentPage <= 1}
                className="rounded-full border border-line px-2.5 py-1 font-semibold disabled:opacity-40">Prev</button>
              <span className="tabular-nums font-semibold">Page {currentPage} of {numPages}</span>
              <button type="button" onClick={() => jumpToPage(Math.min(numPages, currentPage + 1))} disabled={currentPage >= numPages}
                className="rounded-full border border-line px-2.5 py-1 font-semibold disabled:opacity-40">Next</button>
            </div>

            {/* Gate — states BOTH conditions, updates live for assistive tech (§5/§8). */}
            <div id="sign-gate" aria-live="polite" className="rounded-lg border border-line bg-[#F7F9F0] px-3 py-2.5 mb-4">
              <div className={`${FONT} text-[12px] font-semibold text-ink mb-1.5`}>{gateText}</div>
              <div className="flex items-center gap-3">
                <ProgressBar label="Pages read" done={viewedPages.size} total={numPages} />
                <ProgressBar label="Fields" done={doneFields} total={totalFields} />
              </div>
              {!allPagesViewed && (
                <p className={`${FONT} text-[11px] text-[#7A4E10] mt-2 leading-snug`}>
                  {pagesOutstanding} page{pagesOutstanding === 1 ? '' : 's'} still to read. You'll be able to sign once you've been through the whole document.
                </p>
              )}
            </div>

            {/* Fields */}
            <div className="flex flex-col gap-3">
              {railFields.map(f => (
                <RailField key={f.id} f={f} value={values[f.id]} todayLabel={todayLabel} disabled={submitting}
                  onChange={(v) => setValue(f.id, v)} onSign={() => setSigningField(f.id)} />
              ))}
            </div>

            {/* Authority to sign — binds the company (brief-hosted-forms §4). */}
            <label className={`${FONT} mt-4 flex items-start gap-2.5 rounded-lg px-3 py-2.5 cursor-pointer border ${authorityConfirmed ? 'bg-[#E9F3DC] border-[#C9E3AC]' : 'bg-white border-line'}`}>
              <input type="checkbox" checked={authorityConfirmed} disabled={submitting}
                onChange={e => setAuthorityConfirmed(e.target.checked)} className="mt-0.5 w-4 h-4 accent-[#2F6E14] flex-shrink-0" />
              <span className="text-[12.5px] text-ink leading-snug">
                I confirm I have the authority to sign this document on behalf of <span className="font-bold">{companyLabel}</span>. <span className="text-[#D8341A] font-bold">*</span>
              </span>
            </label>

            {signaturePage && (
              <button type="button" onClick={() => jumpToPage(signaturePage)}
                className={`${FONT} mt-3 w-full text-[11px] font-semibold text-[#2E8FA1] hover:underline text-left`}>
                → Jump to the signature page (page {signaturePage})
              </button>
            )}
          </div>

          <div className="p-4 border-t border-line bg-[#F7F9F0] flex-shrink-0">
            <button type="button" onClick={handleSubmit} disabled={!canSubmit} aria-describedby="sign-gate"
              className={`${FONT} w-full text-[12px] font-bold uppercase tracking-[0.14em] rounded-full px-5 py-2.5 transition-colors ${canSubmit ? 'text-white bg-[#2E8FA1] hover:brightness-110' : 'text-white bg-[#B7C0A8] cursor-not-allowed'}`}>
              {submitting ? 'Submitting…' : 'Sign & submit'}
            </button>
            {!canSubmit && !submitting && (
              <p className={`${FONT} text-[11px] text-[#51534A] text-center mt-2`}>{gateText}</p>
            )}
          </div>
        </aside>
      </div>

      {signingField && (
        <SignatureCapture
          onCancel={() => setSigningField(null)}
          onDone={(dataUrl) => { setValue(signingField, dataUrl); setSigningField(null); }} />
      )}
    </div>
  );
}

// A slim two-tone progress bar with an accessible label.
function ProgressBar({ label, done, total }) {
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  return (
    <div className="flex-1 min-w-0">
      <div className={`${FONT} flex items-center justify-between text-[10px] text-[#51534A] mb-0.5`}>
        <span className="truncate">{label}</span>
        <span className="tabular-nums flex-shrink-0">{done}/{total}</span>
      </div>
      <div className="h-1.5 rounded-full bg-[#E6EADB] overflow-hidden" role="progressbar" aria-label={`${label}: ${done} of ${total}`} aria-valuenow={done} aria-valuemin={0} aria-valuemax={total}>
        <div className="h-full bg-[#2E8FA1] transition-[width]" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

// One field in the rail — a real, labelled form control (not an overlay). The
// value is flattened back onto the PDF at the field's placed coords on submit.
function RailField({ f, value, todayLabel, disabled, onChange, onSign }) {
  const req = f.required && f.type !== 'date_signed';
  const mark = req ? <span className="text-[#D8341A] font-bold"> *</span> : null;
  const labelRow = (
    <span className={`${FONT} text-[11px] font-bold uppercase tracking-[0.1em] text-[#51534A]`}>{f.label}{mark}</span>
  );

  if (f.type === 'signature') {
    const signed = !!value;
    return (
      <div>
        <div className="mb-1">{labelRow}</div>
        <button type="button" onClick={onSign} disabled={disabled}
          className={`w-full rounded-lg border ${signed ? 'border-[#C9E3AC] bg-[#FBFDF8]' : 'border-dashed border-[#2E8FA1] bg-[rgba(46,143,161,0.06)]'} h-[64px] flex items-center justify-center overflow-hidden`}>
          {signed ? <img src={value} alt="Your signature" style={{ maxHeight: '58px', maxWidth: '90%' }} />
            : <span className={`${FONT} text-[12px] font-bold text-[#2E8FA1]`}>✍ Sign here</span>}
        </button>
        {signed && <button type="button" onClick={onSign} className={`${FONT} text-[10px] font-semibold text-[#2E8FA1] hover:underline mt-1`}>Re-sign</button>}
      </div>
    );
  }

  if (f.type === 'date_signed') {
    return (
      <div>
        <div className="mb-1">{labelRow}</div>
        <div className={`${FONT} text-[13px] text-[#6B7566] rounded-lg border border-dashed ${''} px-3 py-2 bg-[#F4F6EA]`} style={{ borderColor: NEUTRAL }}>
          {todayLabel} <span className="text-[11px]">· recorded automatically</span>
        </div>
      </div>
    );
  }

  if (f.type === 'tick') {
    return (
      <label className={`${FONT} flex items-center gap-2.5 cursor-pointer rounded-lg border px-3 py-2.5 ${value ? 'bg-[#E9F3DC] border-[#C9E3AC]' : 'bg-white border-line'}`}>
        <input type="checkbox" checked={!!value} disabled={disabled} onChange={e => onChange(e.target.checked)}
          className="w-4 h-4 flex-shrink-0" style={{ accentColor: GREEN }} />
        <span className="text-[12.5px] text-ink">{f.label}{mark}</span>
      </label>
    );
  }

  const prefilled = f.type === 'identity';
  const filled = String(value ?? '').trim().length > 0;
  return (
    <div>
      <div className="mb-1">{labelRow}</div>
      <input
        type={f.type === 'date' ? 'date' : 'text'}
        value={value ?? ''}
        disabled={disabled}
        placeholder={f.label}
        aria-label={f.label}
        onChange={e => onChange(e.target.value)}
        className={`${FONT} w-full text-[13px] rounded-lg border px-3 py-2 outline-none focus:border-[#2E8FA1]`}
        style={{ borderColor: filled ? '#C9E3AC' : NEUTRAL, background: filled ? '#FBFDF8' : '#fff', color: '#2A2E24' }} />
      {prefilled && <p className={`${FONT} text-[10px] text-[#9BA08C] mt-1`}>From your account — edit if it's wrong.</p>}
    </div>
  );
}
